import type {UIEvent} from "react";
import {useState} from "react";

export type CguChange = {date: string; text: string; href?: string; linkLabel?: string};

type Props = {
  changes: Array<CguChange & {version: number}>;
};

export function CguChangesList({changes}: Props) {
  const [hasMore, setHasMore] = useState(false);
  const scrollable = changes.length > 1;

  return (
    <div
      style={{
        background: "rgba(var(--mt-color-action-rgb), 0.06)",
        border: "1px solid rgba(var(--mt-color-action-rgb), 0.18)",
        borderRadius: 8,
        marginBottom: "1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 12px",
          fontWeight: 700,
          color: "var(--mt-color-action)",
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: ".03em",
        }}
      >
        <span>
          {"Ce qui change"}
          {changes.length > 1 && (
            <span
              style={{
                fontWeight: 400,
                color: "var(--bs-gray-600)",
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              {` (${changes.length} modifications)`}
            </span>
          )}
        </span>
      </div>
      <div style={{position: "relative"}}>
        <div
          ref={handleListRef}
          onScroll={handleScroll}
          tabIndex={scrollable ? 0 : undefined}
          role="region"
          aria-label="Ce qui change"
          style={scrollable ? {maxHeight: 110, overflowY: "auto"} : undefined}
        >
          <ul
            style={{
              margin: 0,
              padding: "0 12px 10px",
              listStyle: "none",
            }}
          >
            {changes.map((change, index) => (
              <li
                key={change.version}
                style={{
                  padding: "5px 0",
                  borderTop:
                    index === 0 ? undefined : "1px solid rgba(var(--mt-color-action-rgb), 0.12)",
                }}
              >
                <span style={{color: "var(--bs-gray-600)", fontSize: 12}}>{change.date}</span>
                <br />
                {change.text}
                {change.href && (
                  <>
                    {" "}
                    <a href={change.href} target="_blank" rel="noopener noreferrer">
                      {change.linkLabel ?? "Lire"}
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
        {scrollable && hasMore && (
          <div
            data-testid="cgu-changes-fade"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 26,
              pointerEvents: "none",
              borderRadius: "0 0 8px 8px",
              background: "linear-gradient(to bottom, rgba(244,242,251,0), #f4f2fb)",
            }}
          />
        )}
      </div>
    </div>
  );

  function computeHasMore(node: HTMLDivElement): boolean {
    return node.scrollHeight - node.clientHeight - node.scrollTop > 2;
  }

  function handleListRef(node: HTMLDivElement | null) {
    if (!node) {
      return;
    }
    setHasMore(computeHasMore(node));
  }

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    setHasMore(computeHasMore(event.currentTarget));
  }
}
